import { esc } from "./escape.js";

// Shared photo + emoji picker markup/wiring for the Add and Edit menu-item
// forms (pointbunny-app-todos.md, requested 2026-08-19). Both panels are
// always visible and interactive at once (no Photo/Emoji tab switch - removed
// 2026-08-20, since a photo and an emoji are set through two entirely
// different controls anyway, a mode switch was an extra click for no reason).
// `image_url`/`icon_emoji` are still mutually exclusive server-side though
// (see model.js uploadNewMenuItem/updateMenuItem) - whichever the owner
// touched LAST decides which one wins at submit time, tracked via the hidden
// `imageMode` field: selecting an emoji sets it to "emoji", choosing a photo
// (click-browse or drag-drop) sets it back to "photo". Both forms submit via
// FormData, so `imageMode`/`iconEmoji` flow straight into the
// controller/model without any extra plumbing.
//
// The emoji side is a COMPACT picker (owner's pick from the 2026-10-02 design
// round, "Compact button"): the form shows one tidy row - the chosen emoji
// plus "Tap to change" - and tapping it opens a small inline popover with a
// name search (pasting any emoji works too), icon category tabs, the grid and
// "Remove emoji". Picking closes it. Every rule in pointbunny.css is scoped
// under .item-emoji-panel so the Add form's generic `.add-menu-form button`
// (solid green CTA) can't restyle the picker's buttons again.

export const EMOJI_GROUPS = [
  { id: "food", label: "Food", icon: "🍔", emojis: ["🍔", "🍕", "🌭", "🌮", "🌯", "🍗", "🍖", "🥪", "🍜", "🍲", "🍱", "🍣", "🍤", "🥗", "🍳", "🥞", "🧇", "🥓", "🧀", "🍦", "🍨", "🍩", "🍪", "🎂", "🧁", "🍫", "🍿", "🥐", "🥯", "🍎", "🍌", "🍇"] },
  { id: "drinks", label: "Drinks", icon: "☕", emojis: ["☕", "🍵", "🧋", "🥤", "🧃", "🧉", "🍺", "🍷", "🍸", "🍹", "🥂", "🥛", "🧊"] },
  { id: "shop", label: "Shop", icon: "🛍️", emojis: ["👕", "👗", "👟", "👜", "💄", "💇", "✂️", "🧴", "🧼", "🕯️", "📷", "🎧", "📱", "💻", "🎁", "🛍️", "🧸", "📚", "🖊️", "🔧", "🔨", "🚗", "🧹", "🐾", "🌸", "💐"] },
  { id: "other", label: "Other", icon: "✨", emojis: ["⭐", "✨", "💎", "🔥", "❤️", "🎉", "🏆", "🎯", "📦", "✅", "🍀", "🎈"] },
];

// Search keywords per emoji (lowercase, space-separated). Matching is by word
// prefix or substring, so "cof" finds coffee and "matcha" finds tea.
const EMOJI_NAMES = {
  "🍔": "burger hamburger", "🍕": "pizza", "🌭": "hot dog hotdog", "🌮": "taco", "🌯": "burrito wrap", "🍗": "chicken drumstick", "🍖": "meat ribs", "🥪": "sandwich", "🍜": "noodles ramen pho", "🍲": "soup stew", "🍱": "bento rice meal", "🍣": "sushi", "🍤": "shrimp tempura", "🥗": "salad", "🍳": "egg breakfast", "🥞": "pancakes", "🧇": "waffle", "🥓": "bacon", "🧀": "cheese", "🍦": "ice cream soft serve", "🍨": "ice cream gelato", "🍩": "donut doughnut", "🍪": "cookie", "🎂": "cake birthday", "🧁": "cupcake cake muffin", "🍫": "chocolate", "🍿": "popcorn", "🥐": "croissant bread pastry", "🥯": "bagel bread", "🍎": "apple fruit", "🍌": "banana fruit", "🍇": "grapes fruit",
  "☕": "coffee latte espresso cappuccino americano hot", "🍵": "tea matcha green hot", "🧋": "boba milk tea bubble", "🥤": "soda cup iced drink smoothie", "🧃": "juice box", "🧉": "mate", "🍺": "beer", "🍷": "wine", "🍸": "cocktail martini", "🍹": "tropical drink cocktail", "🥂": "champagne toast", "🥛": "milk", "🧊": "ice iced",
  "👕": "shirt tshirt clothes", "👗": "dress clothes", "👟": "shoe sneaker", "👜": "bag purse", "💄": "lipstick makeup beauty", "💇": "haircut salon hair", "✂️": "scissors cut trim", "🧴": "lotion bottle skincare", "🧼": "soap wash", "🕯️": "candle", "📷": "camera photo", "🎧": "headphones audio", "📱": "phone mobile", "💻": "laptop computer", "🎁": "gift present", "🛍️": "shopping bags", "🧸": "toy teddy", "📚": "books", "🖊️": "pen", "🔧": "wrench repair tool", "🔨": "hammer tool", "🚗": "car auto", "🧹": "broom cleaning", "🐾": "pet paw", "🌸": "flower blossom", "💐": "bouquet flowers",
  "⭐": "star", "✨": "sparkles special", "💎": "gem diamond", "🔥": "fire hot spicy", "❤️": "heart love", "🎉": "party celebration", "🏆": "trophy award", "🎯": "target", "📦": "box package", "✅": "check done", "🍀": "clover luck", "🎈": "balloon",
};

const ALL_EMOJIS = EMOJI_GROUPS.flatMap((g) => g.emojis);
const findGroup = (emoji) =>
  EMOJI_GROUPS.find((g) => g.emojis.includes(emoji))?.id ?? EMOJI_GROUPS[0].id;
const isEmoji = (s) => /\p{Extended_Pictographic}/u.test(s);
// First user-perceived character (keeps multi-codepoint emoji like 🕯️ whole).
const firstGrapheme = (s) => {
  if (typeof Intl !== "undefined" && Intl.Segmenter) {
    for (const { segment } of new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(s)) {
      if (isEmoji(segment)) return segment;
    }
    return "";
  }
  return [...s].find(isEmoji) ?? "";
};

const optMarkup = (e, selected) =>
  `<button type="button" class="item-emoji-opt${e === selected ? " item-emoji-opt--selected" : ""}" data-emoji="${e}" aria-label="${esc(EMOJI_NAMES[e]?.split(" ")[0] ?? e)}" title="${esc(EMOJI_NAMES[e]?.split(" ")[0] ?? "")}">${e}</button>`;

const TRIGGER_SUB_EMPTY = "Optional · shown on New Order in Emoji view";
const TRIGGER_SUB_SET = "Tap to change";

export const emojiPanelMarkup = function ({ selectedEmoji, extraClass = "" } = {}) {
  const activeCat = findGroup(selectedEmoji);
  const has = !!selectedEmoji;
  return `
    <div class="item-emoji-panel${extraClass ? ` ${extraClass}` : ""}">
      <button type="button" class="ivp-trigger" aria-expanded="false" aria-haspopup="true">
        <span class="item-emoji-preview${has ? "" : " item-emoji-preview--empty"}" aria-hidden="true">${has ? esc(selectedEmoji) : "🙂"}</span>
        <span class="ivp-trigger-text">
          <span class="ivp-trigger-title">Emoji</span>
          <span class="ivp-trigger-sub">${has ? TRIGGER_SUB_SET : TRIGGER_SUB_EMPTY}</span>
        </span>
        <svg class="ivp-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>
      </button>
      <div class="ivp-pop hidden">
        <input type="text" class="item-emoji-custom-input" maxlength="40" placeholder="Search, or paste any emoji" aria-label="Search emoji" autocomplete="off" />
        <div class="item-emoji-cats" role="tablist">
          ${EMOJI_GROUPS.map((g) => `<button type="button" class="item-emoji-cat${g.id === activeCat ? " item-emoji-cat--active" : ""}" data-emoji-cat="${g.id}" role="tab" aria-label="${esc(g.label)}" title="${esc(g.label)}">${g.icon}</button>`).join("")}
        </div>
        ${EMOJI_GROUPS.map((g) => `
          <div class="item-emoji-grid${g.id === activeCat ? "" : " hidden"}" data-emoji-grid="${g.id}">
            ${g.emojis.map((e) => optMarkup(e, selectedEmoji)).join("")}
          </div>`).join("")}
        <div class="item-emoji-grid hidden" data-emoji-grid="__search"></div>
        <div class="ivp-foot">
          <span class="ivp-hint">Tip: paste any emoji into the search</span>
          <button type="button" class="ivp-clear${has ? "" : " hidden"}">Remove emoji</button>
        </div>
      </div>
    </div>`;
};

// The two form fields the controller/model actually read.
export const visualHiddenInputsMarkup = function ({ mode, selectedEmoji }) {
  return `
    <input type="hidden" name="imageMode" class="item-image-mode-input" value="${mode}" />
    <input type="hidden" name="iconEmoji" value="${esc(selectedEmoji || "")}" />`;
};

const setOpen = (panel, open) => {
  if (!panel) return;
  panel.querySelector(".ivp-pop")?.classList.toggle("hidden", !open);
  panel.querySelector(".ivp-trigger")?.setAttribute("aria-expanded", String(open));
  panel.classList.toggle("item-emoji-panel--open", open);
  if (open) {
    resetSearch(panel);
    // Focus the search on pointer devices only - on a tablet it would pop the
    // keyboard over the grid the user is about to tap.
    if (window.matchMedia?.("(hover: hover)").matches) panel.querySelector(".item-emoji-custom-input")?.focus();
  }
};

const closeAllPickers = (except = null) => {
  document.querySelectorAll(".item-emoji-panel--open").forEach((p) => { if (p !== except) setOpen(p, false); });
};

const showCategory = (panel, cat) => {
  panel.querySelectorAll(".item-emoji-cat").forEach((b) => b.classList.toggle("item-emoji-cat--active", b.dataset.emojiCat === cat));
  panel.querySelectorAll("[data-emoji-grid]").forEach((g) => g.classList.toggle("hidden", g.dataset.emojiGrid !== cat));
};

const resetSearch = (panel) => {
  const input = panel.querySelector(".item-emoji-custom-input");
  if (input) input.value = "";
  const selected = panel.closest("form")?.querySelector('input[name="iconEmoji"]')?.value;
  showCategory(panel, findGroup(selected));
};

const runSearch = (panel, query) => {
  const q = query.trim().toLowerCase();
  if (!q) { resetSearch(panel); return; }
  // A pasted/typed emoji is picked straight away (the old "paste any emoji").
  const pasted = firstGrapheme(query);
  if (pasted) { selectEmoji(panel, pasted); setOpen(panel, false); return; }
  const selected = panel.closest("form")?.querySelector('input[name="iconEmoji"]')?.value;
  const hits = ALL_EMOJIS.filter((e) => {
    const words = (EMOJI_NAMES[e] ?? "").split(" ");
    return words.some((w) => w.startsWith(q)) || (EMOJI_NAMES[e] ?? "").includes(q);
  });
  const grid = panel.querySelector('[data-emoji-grid="__search"]');
  grid.innerHTML = hits.length
    ? hits.map((e) => optMarkup(e, selected)).join("")
    : `<p class="ivp-empty">No match for "${esc(query.trim())}". Paste any emoji instead.</p>`;
  panel.querySelectorAll(".item-emoji-cat").forEach((b) => b.classList.remove("item-emoji-cat--active"));
  panel.querySelectorAll("[data-emoji-grid]").forEach((g) => g.classList.toggle("hidden", g !== grid));
};

// Click-outside closes any open picker. One document listener for the app.
let _outsideWired = false;
const wireOutsideClose = () => {
  if (_outsideWired) return;
  _outsideWired = true;
  document.addEventListener("click", (e) => {
    if (e.target.closest(".item-emoji-panel")) return;
    closeAllPickers();
  });
};

// One delegated listener set, attached once to a persistent container
// (the modal/form parent), covers every re-render of the form inside it.
export const wireVisualPicker = function (root) {
  wireOutsideClose();

  root.addEventListener("click", (e) => {
    const trigger = e.target.closest(".ivp-trigger");
    if (trigger) {
      const panel = trigger.closest(".item-emoji-panel");
      const open = !panel.classList.contains("item-emoji-panel--open");
      closeAllPickers(panel);
      setOpen(panel, open);
      return;
    }

    const catBtn = e.target.closest(".item-emoji-cat");
    if (catBtn) {
      const panel = catBtn.closest(".item-emoji-panel");
      const input = panel.querySelector(".item-emoji-custom-input");
      if (input) input.value = "";
      showCategory(panel, catBtn.dataset.emojiCat);
      return;
    }

    const opt = e.target.closest(".item-emoji-opt");
    if (opt) {
      const panel = opt.closest(".item-emoji-panel");
      selectEmoji(panel, opt.dataset.emoji);
      setOpen(panel, false);
      return;
    }

    const clear = e.target.closest(".ivp-clear");
    if (clear) {
      const panel = clear.closest(".item-emoji-panel");
      selectEmoji(panel, "");
      // Back to Photo so an existing photo on this item is what saves.
      const modeInput = panel.closest("form")?.querySelector('input[name="imageMode"]');
      if (modeInput) modeInput.value = "photo";
      setOpen(panel, false);
    }
  });

  root.addEventListener("input", (e) => {
    const input = e.target.closest(".item-emoji-custom-input");
    if (!input) return;
    runSearch(input.closest(".item-emoji-panel"), input.value);
  });

  // A successfully chosen photo makes Photo the mode that saves - the mirror
  // of selectEmoji flipping it to "emoji" below. Covers native file-picker
  // selection directly; the Edit form's drag-and-drop handler
  // (menuEditView.js _addPhotoDropZone) dispatches this same change event
  // after assigning `input.files`, so it's covered too.
  root.addEventListener("change", (e) => {
    const input = e.target.closest('input[type="file"][name="image"]');
    if (!input || !input.files?.[0]) return;
    const modeInput = input.closest("form")?.querySelector('input[name="imageMode"]');
    if (modeInput) modeInput.value = "photo";
  });

  // Enter in the search picks the only/first match instead of submitting the
  // item form; Escape closes the picker (not the whole item modal).
  root.addEventListener("keydown", (e) => {
    const panel = e.target.closest(".item-emoji-panel");
    if (!panel) return;
    if (e.key === "Escape" && panel.classList.contains("item-emoji-panel--open")) {
      e.preventDefault();
      e.stopPropagation();
      setOpen(panel, false);
      panel.querySelector(".ivp-trigger")?.focus();
      return;
    }
    if (e.key === "Enter" && e.target.closest(".item-emoji-custom-input")) {
      e.preventDefault();
      const first = panel.querySelector('[data-emoji-grid]:not(.hidden) .item-emoji-opt');
      if (first) { selectEmoji(panel, first.dataset.emoji); setOpen(panel, false); }
    }
  });
};

const selectEmoji = function (panel, emoji) {
  if (!panel) return;
  const form = panel.closest("form");
  const emojiInput = form?.querySelector('input[name="iconEmoji"]');
  if (emojiInput) emojiInput.value = emoji;
  if (emoji) {
    const modeInput = form?.querySelector('input[name="imageMode"]');
    if (modeInput) modeInput.value = "emoji";
  }
  const preview = panel.querySelector(".item-emoji-preview");
  if (preview) {
    preview.textContent = emoji || "🙂";
    preview.classList.toggle("item-emoji-preview--empty", !emoji);
  }
  const sub = panel.querySelector(".ivp-trigger-sub");
  if (sub) sub.textContent = emoji ? TRIGGER_SUB_SET : TRIGGER_SUB_EMPTY;
  panel.querySelector(".ivp-clear")?.classList.toggle("hidden", !emoji);
  panel.querySelectorAll(".item-emoji-opt").forEach((b) => b.classList.toggle("item-emoji-opt--selected", b.dataset.emoji === emoji));
};

// Used by the Add form's reset-on-close: clears any staged emoji and snaps
// the save-mode back to Photo, so a reopened form never carries over a
// discarded pick.
export const resetVisualPicker = function (root) {
  const panel = root.querySelector(".item-emoji-panel");
  if (panel) { selectEmoji(panel, ""); setOpen(panel, false); }
  const modeInput = root.querySelector('input[name="imageMode"]');
  if (modeInput) modeInput.value = "photo";
};
